import React, { useState, useCallback, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ArrowBack as BackIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import reportService from "../services/reportService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TextField, MenuItem } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/apiService";
import {
  arabicRegularBase64,
  arabicBoldBase64,
} from "../assets/fonts/Arabicfontbase64";

const formatDate = (date) => {
  if (!date || isNaN(new Date(date).getTime())) return undefined;

  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatCurrency = (value) =>
  Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SALES_ROWS = [
  {
    id: 1,
    key: "standardSales",
    en: "Standard rated sales",
    ar: "المبيعات الخاضعة للنسبة الأساسية",
    type: "sales",
    index: 0,
  },
  // { id: 2, key: "privateSales", en: "Private Healthcare / Private Education sales to citizens", ar: "مبيعات الرعاية الصحية والتعليم الخاص", type: "empty" },
  {
    id: 3,
    key: "zeroRatedSales",
    en: "Zero rated domestic sales",
    ar: "المبيعات المحلية ذات النسبة الصفرية",
    type: "sales",
    index: 1,
  },
  // { id: 4, key: "exports", en: "Exports", ar: "الصادرات", type: "empty" },
  {
    id: 5,
    key: "exemptSales",
    en: "Exempt sales",
    ar: "المبيعات المعفاة",
    type: "sales",
    index: 2,
  },
  {
    id: 6,
    key: "totalSales",
    en: "Total Sales",
    ar: "إجمالي المبيعات",
    type: "sales",
    index: 3,
    total: true,
  },
];

const PURCHASE_ROWS = [
  {
    id: 7,
    key: "standardPurchases",
    en: "Standard rated domestic purchases",
    ar: "المشتريات المحلية الخاضعة للنسبة الأساسية",
    type: "purchases",
    index: 0,
  },
  // { id: 8, key: "imports", en: "Imports subject to VAT paid at customs", ar: "الواردات الخاضعة لضريبة القيمة المضافة المدفوعة في الجمارك", type: "empty" },
  // { id: 9, key: "reverseCharge", en: "Imports subject to VAT accounted for through reverse charge mechanism", ar: "الواردات الخاضعة لآلية الاحتساب العكسي", type: "empty" },
  {
    id: 8,
    key: "zeroRatedPurchases",
    en: "Zero rated purchases",
    ar: "المشتريات ذات النسبة الصفرية",
    type: "purchases",
    index: 1,
  },
  {
    id: 9,
    key: "exemptPurchases",
    en: "Exempt purchases",
    ar: "المشتريات المعفاة",
    type: "purchases",
    index: 2,
  },
  {
    id: 10,
    key: "totalPurchases",
    en: "Total purchases",
    ar: "إجمالي المشتريات",
    type: "purchases",
    index: 3,
    total: true,
  },
];

const SUMMARY_ROWS = [
  {
    id: 11,
    key: "currentVAT",
    en: "Total VAT due for current period",
    ar: "إجمالي ضريبة القيمة المضافة المستحقة للفترة الحالية",
    type: "summary",
    index: 0,
  },
  // { id: 14, key: "previousAdjustment", en: "Corrections from previous period (between SAR ±5,000)", ar: "تعديلات الفترة السابقة", type: "empty" },
  // { id: 15, key: "vatCredit", en: "VAT credit carried forward from previous period(s)", ar: "رصيد ضريبة القيمة المضافة المرحل من الفترات السابقة", type: "empty" },
  {
    id: 12,
    key: "netVAT",
    en: "Net VAT due (or claim)",
    ar: "صافي ضريبة القيمة المضافة المستحقة أو المطالب بها",
    type: "summary",
    index: 1,
    total: true,
  },
];

const VAT_Detail = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const isRtl = language === "ar";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const { currentUser } = useAuth();
  const isVatAdmin = currentUser?.role === "vatadmin";

  const [companies, setCompanies] = useState([]);

  const [filters, setFilters] = useState({
    groupBy: "month",
    startDate: null,
    endDate: null,
    tenantId: "all",
  });

  const fetchCompanies = async () => {
    try {
      const res = await api.get("/vat-admin/my-companies", {
        params: { limit: "all" },
      });

      const records = res.data?.data ?? res.data ?? [];

      setCompanies(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  useEffect(() => {
    if (isVatAdmin) {
      fetchCompanies();
    }
  }, [isVatAdmin]);

  const handleExportPDF = () => {
    if (!report) {
      setError(
        isRtl
          ? "يرجى تطبيق التصفية وتحميل تقرير ضريبة القيمة المضافة أولاً."
          : "Please apply filters and load the VAT report first.",
      );
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      if (arabicRegularBase64) {
        doc.addFileToVFS("Amiri-Regular.ttf", arabicRegularBase64);
        doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
      }
      if (arabicBoldBase64) {
        doc.addFileToVFS("Amiri-Bold.ttf", arabicBoldBase64);
        doc.addFont("Amiri-Bold.ttf", "Amiri", "bold");
      }

      // Title
      doc.setFontSize(16);
      if (arabicBoldBase64) doc.setFont("Amiri", "bold");
      else doc.setFont("helvetica", "bold");
      doc.setTextColor(18, 107, 86);
      doc.text(isRtl ? "تفاصيل الإقرار الضريبي" : "VAT Return Details", 14, 16);

      // Period and Generation metadata
      doc.setFontSize(9);
      if (arabicRegularBase64) doc.setFont("Amiri", "normal");
      else doc.setFont("helvetica", "normal");
      doc.setTextColor(90, 90, 90);

      const startStr =
        formatDate(filters.startDate) || (isRtl ? "الكل" : "All");
      const endStr = formatDate(filters.endDate) || (isRtl ? "الكل" : "All");
      const periodText = isRtl
        ? `الفترة: من ${startStr} إلى ${endStr}`
        : `Period: From ${startStr} To ${endStr}`;

      const generatedDate = formatDate(new Date());
      const generatedText = isRtl
        ? `تاريخ الإنشاء: ${generatedDate}`
        : `Generated: ${generatedDate}`;

      doc.text(periodText, 14, 23);
      doc.text(generatedText, doc.internal.pageSize.getWidth() - 14, 23, {
        align: "right",
      });

      const tableHead = [
        [
          isRtl ? "البند" : "VAT Return Details",
          isRtl ? "المبلغ (ريال)" : "Amount (SAR)",
          isRtl ? "التعديل (ريال)" : "Adjustment (SAR)",
          isRtl ? "مبلغ الضريبة (ريال)" : "VAT Amount (SAR)",
        ],
      ];

      const tableBody = [];

      const pushSectionHeader = (title, color) => {
        tableBody.push([
          {
            content: title,
            colSpan: 4,
            styles: {
              fillColor: color,
              textColor: [255, 255, 255],
              fontStyle: "bold",
              fontSize: 9,
              cellPadding: 2.5,
              halign: isRtl ? "right" : "left",
            },
          },
        ]);
      };

      const pushSectionRows = (rows, dataArray, isSummarySection = false) => {
        rows.forEach((row) => {
          const rowData = dataArray?.[row.index];
          const isTotal = !!row.total;
          const label = isRtl ? row.ar : row.en;

          let amountVal = "-";
          let adjVal = "-";
          let vatVal = "-";

          if (isSummarySection) {
            amountVal = formatCurrency(rowData?.amount);
            adjVal = "-";
            vatVal = formatCurrency(rowData?.amount);
          } else {
            amountVal = formatCurrency(rowData?.amount);
            adjVal = formatCurrency(rowData?.adjustment);
            vatVal = formatCurrency(rowData?.vatAmount);
          }

          const cellStyles = isTotal
            ? {
                fillColor: [203, 161, 62],
                textColor: [255, 255, 255],
                fontStyle: "bold",
              }
            : undefined;

          tableBody.push([
            { content: label, styles: cellStyles },
            { content: amountVal, styles: cellStyles },
            { content: adjVal, styles: cellStyles },
            { content: vatVal, styles: cellStyles },
          ]);
        });
      };

      // 1. Sales Section
      pushSectionHeader(
        isRtl ? "ضريبة المبيعات" : "VAT on sales",
        [18, 107, 86],
      );
      pushSectionRows(SALES_ROWS, report.sales);

      // 2. Purchases Section
      pushSectionHeader(
        isRtl ? "ضريبة المشتريات" : "VAT on purchases",
        [197, 160, 57],
      );
      pushSectionRows(PURCHASE_ROWS, report.purchases);

      // 3. Summary Section
      pushSectionHeader(isRtl ? "ملخص الضريبة" : "VAT summary", [18, 107, 86]);
      pushSectionRows(SUMMARY_ROWS, report.summary, true);

      autoTable(doc, {
        startY: 28,
        head: tableHead,
        body: tableBody,
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          font: arabicRegularBase64 ? "Amiri" : "helvetica",
          textColor: [50, 50, 50],
        },
        headStyles: {
          fillColor: [18, 107, 86],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
          font: arabicBoldBase64 || arabicRegularBase64 ? "Amiri" : "helvetica",
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250],
        },
        columnStyles: {
          0: { halign: isRtl ? "right" : "left", cellWidth: 92 },
          1: { halign: "right", cellWidth: 30 },
          2: { halign: "right", cellWidth: 30 },
          3: { halign: "right", cellWidth: 30 },
        },
        didDrawPage: () => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          if (arabicRegularBase64) {
            doc.setFont("Amiri", "normal");
          } else {
            doc.setFont("helvetica", "normal");
          }
          const pageStr = isRtl
            ? `صفحة ${doc.internal.getCurrentPageInfo().pageNumber} من ${pageCount}`
            : `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`;
          doc.text(
            pageStr,
            doc.internal.pageSize.getWidth() - 14,
            doc.internal.pageSize.getHeight() - 8,
            { align: "right" },
          );
        },
      });

      // Status Note below the table
      if (report && report.summary && report.summary[1]?.status) {
        const netStatus = report.summary[1].status;
        const isPayable = netStatus === "payable";
        const finalY = doc.lastAutoTable.finalY + 8;

        doc.setFontSize(10);
        if (arabicBoldBase64) doc.setFont("Amiri", "bold");
        else doc.setFont("helvetica", "bold");

        if (isPayable) {
          doc.setTextColor(211, 47, 47);
        } else {
          doc.setTextColor(46, 125, 50);
        }

        const statusText = isRtl
          ? isPayable
            ? `صافي الضريبة المستحقة للدفع: ${formatCurrency(report.summary[1].amount)} ريال`
            : `صافي الضريبة المستردة: ${formatCurrency(report.summary[1].amount)} ريال`
          : isPayable
            ? `Net VAT Payable: SAR ${formatCurrency(report.summary[1].amount)}`
            : `Net VAT Refundable: SAR ${formatCurrency(report.summary[1].amount)}`;

        doc.text(statusText, doc.internal.pageSize.getWidth() - 14, finalY, {
          align: "right",
        });
      }

      const fileStart = formatDate(filters.startDate) || "All";
      const fileEnd = formatDate(filters.endDate) || "All";
      doc.save(`VAT_Return_Report_${fileStart}_${fileEnd}.pdf`);
    } catch (err) {
      console.error("VAT return PDF export failed:", err);
      setError(
        isRtl
          ? "فشل في تصدير تقرير ضريبة القيمة المضافة كملف PDF."
          : "Failed to export VAT report to PDF.",
      );
    }
  };

  const fetchReport = useCallback(async () => {
    if (loading) return;

    try {
      setLoading(true);
      setError(null);

      const params = {
        startDate: formatDate(filters.startDate),
        endDate: formatDate(filters.endDate),
        language: isRtl ? "ar" : "en",
        ...(isVatAdmin &&
          filters.tenantId !== "all" && {
            tenantIds: [filters.tenantId],
          }),
      };

      const response = await reportService.getVATDetail(params);

      // Support both Axios response.data and direct API response.
      const body = response?.data ?? response;
      const payload = body?.data ?? body;

      if (body?.success === false) {
        throw new Error(body?.message || "Failed to fetch VAT return");
      }

      setReport({
        sales: Array.isArray(payload?.sales) ? payload.sales : [],
        purchases: Array.isArray(payload?.purchases) ? payload.purchases : [],
        summary: Array.isArray(payload?.summary) ? payload.summary : [],
        filters: body?.filters ?? payload?.filters ?? {},
      });
    } catch (err) {
      console.error("Error fetching VAT return:", err);
      setReport(null);
      setError(err?.message || t("Reports.Failed_To_Fetch_VAT_Detail"));
    } finally {
      setLoading(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    isRtl,
    loading,
    filters.tenantId,
    isVatAdmin,
    loading,
    t,
  ]);

  const getRowData = (row) => {
    if (!report || row.type === "empty") return null;

    if (row.type === "sales") {
      return report.sales[row.index] ?? null;
    }

    if (row.type === "purchases") {
      return report.purchases[row.index] ?? null;
    }

    if (row.type === "summary") {
      return report.summary[row.index] ?? null;
    }

    return null;
  };

  const renderAmount = (value) => {
    if (value === null || value === undefined) return "";

    return formatCurrency(value);
  };

  const renderRow = (row) => {
    const data = getRowData(row);
    const isSummary = row.type === "summary";
    const isTotal = Boolean(row.total);

    return (
      <Grid
        container
        key={row.id}
        alignItems="center"
        sx={{
          minHeight: isTotal ? 37 : 30,
          borderBottom: "1px solid #e6e6e6",
          bgcolor: isTotal ? "#f7f3e7" : "#fff",
        }}
      >
        <Grid item xs={12} sm={4.6} md={4.4}>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 12,
              fontWeight: isTotal ? 700 : 400,
              color: "#286b59",
              // direction: isRtl ? "rtl" : "ltr",
            }}
          >
            <Box component="span" sx={{ minWidth: 17 }}>
              {row.id}-
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                lineHeight: 1.35,
                fontWeight: isTotal ? 700 : 400,
              }}
            >
              {isRtl ? row.ar : row.en}
            </Typography>
          </Box>
        </Grid>

        {["amount", "adjustment", "vatAmount"].map((field) => {
          const isSummaryField = isSummary && field !== "vatAmount";
          const hasValue =
            data &&
            (field === "amount" ||
              (!isSummaryField && field !== "adjustment") ||
              field === "adjustment");

          const value = isSummary
            ? field === "vatAmount"
              ? data?.amount
              : field === "amount"
                ? data?.amount
                : undefined
            : data?.[field];

          const showValue = data && hasValue && value !== undefined;

          return (
            <Grid item xs={4} sm={2.45} md={2.45} key={field}>
              <Box sx={{ px: 0.5, py: 0.3 }}>
                <Box
                  sx={{
                    minHeight: 23,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    px: 0.75,
                    border: "1px solid",
                    borderColor:
                      isTotal || isSummaryField ? "#c59a32" : "#d6d6d6",
                    borderRadius: "3px",
                    bgcolor: isTotal || isSummaryField ? "#cba13e" : "#fff",
                    color: isTotal || isSummaryField ? "#fff" : "#333",
                    fontSize: 11,
                    fontWeight: isTotal || isSummaryField ? 700 : 400,
                    overflow: "hidden",
                  }}
                >
                  {showValue ? renderAmount(value) : ""}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderSection = (title, rows, color) => (
    <Box
      key={title}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "28px minmax(0, 1fr)",
          sm: "34px minmax(0, 1fr)",
        },
        mb: 1,
        border: "1px solid #eeeeee",
      }}
    >
      <Box
        sx={{
          bgcolor: color,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          writingMode: isRtl ? "vertical-rl" : "vertical-rl",
          transform: isRtl ? "none" : "none",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          py: 1,
        }}
      >
        {title}
      </Box>

      <Box sx={{ minWidth: 0 }}>{rows.map(renderRow)}</Box>
    </Box>
  );

  return (
    <div sx={{ py: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <IconButton onClick={() => navigate("/reports")}>
          <BackIcon sx={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
        </IconButton>

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {t("Reports.VAT_Detail")}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t("Reports.VAT_Detail_Description")}
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleExportPDF}
          disabled={!report || loading}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            borderRadius: "1px",
          }}
        >
          {isRtl ? "تصدير إلى PDF" : "Export to PDF"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1.5} alignItems="center" wrap="nowrap">
            <Grid item xs={4} sm={isVatAdmin ? 3.5 : 5}>
              <DatePicker
                label={t("Reports.Start_Date")}
                value={filters.startDate}
                onChange={(date) =>
                  setFilters((prev) => ({ ...prev, startDate: date }))
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "1px",
                      },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={4} sm={isVatAdmin ? 3.5 : 5}>
              <DatePicker
                label={t("Reports.End_Date")}
                value={filters.endDate}
                minDate={filters.startDate || undefined}
                onChange={(date) =>
                  setFilters((prev) => ({ ...prev, endDate: date }))
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "1px",
                      },
                    },
                  },
                }}
              />
            </Grid>

            {isVatAdmin && (
              <Grid item xs={4} sm={3.5}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("Common.Company_Name") || "Company"}
                  value={filters.tenantId}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      tenantId: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "1px",
                    },
                  }}
                >
                  <MenuItem value="all">
                    {t("Common.All_Companies") || "All Companies"}
                  </MenuItem>

                  {companies.map((company) => (
                    <MenuItem key={company.tenantId} value={company.tenantId}>
                      {company.name || company.tenantId}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs="auto" sm={isVatAdmin ? 1.5 : 2}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchReport}
                disabled={loading}
                sx={{
                  height: 40,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  borderRadius: "1px",
                  px: 2,
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("Reports.Apply_Filters")
                )}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 0.5, sm: 2 },
          overflowX: "auto",
          // direction: isRtl ? "rtl" : "ltr",
        }}
      >
        <Box sx={{ minWidth: { xs: 580, sm: 650 } }}>
          <Grid
            container
            sx={{
              mb: 0.5,
              alignItems: "center",
              fontSize: 12,
              color: "#555",
            }}
          >
            <Grid item xs={12} sm={4.6} md={4.4}>
              <Typography fontSize={12} fontWeight={700} px={1}>
                {isRtl ? "البند" : "VAT Return Details"}
              </Typography>
            </Grid>
            {[
              isRtl ? "المبلغ (ريال)" : "Amount (SAR)",
              isRtl ? "التعديل (ريال)" : "Adjustment (SAR)",
              isRtl ? "مبلغ الضريبة (ريال)" : "VAT Amount (SAR)",
            ].map((label) => (
              <Grid item xs={4} sm={2.45} md={2.45} key={label}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {label}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {loading && !report ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {renderSection(
                isRtl ? "ضريبة المبيعات" : "VAT on sales",
                SALES_ROWS,
                "#126b56",
              )}

              {renderSection(
                isRtl ? "ضريبة المشتريات" : "VAT on purchases",
                PURCHASE_ROWS,
                "#c5a039",
              )}

              {renderSection(
                isRtl ? "ملخص الضريبة" : "VAT summary",
                SUMMARY_ROWS,
                "#126b56",
              )}

              {report && report.summary[1]?.status && (
                <Typography
                  sx={{
                    mt: 1,
                    textAlign: "end",
                    fontWeight: 700,
                    color:
                      report.summary[1].status === "payable"
                        ? "error.main"
                        : "success.main",
                  }}
                >
                  {isRtl ? "الحالة: " : "Status: "}
                  {report.summary[1].status === "payable"
                    ? isRtl
                      ? "مستحق الدفع"
                      : "Payable"
                    : isRtl
                      ? "قابل للاسترداد"
                      : "Refundable"}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Paper>
    </div>
  );
};

export default VAT_Detail;
